import { currentCountry } from "@/jurisdictions/country";
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, ArrowLeft, Check, Sparkle, ChatCircleText, MagnifyingGlass, Receipt, Percent, LinkSimple, Bank, BookOpen, BellRinging } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

const SECTEURS = ["Distribution", "BTP", "Industrie", "Services", "Immobilier", "Agroalimentaire", "Santé", "Tech / Startups", "Commerce", "Restauration", "Transport", "Textile"];
const CLIENTS = ["TPE", "PME", "Grandes entreprises", "Auto-entrepreneurs", "Associations", "Professions libérales"];
const REGIMES = ["IS - régime normal", "IS - CGE", "Auto-entrepreneur (CPU)", "TVA", "IR salarial", "IR professionnel", "Exonération / CFC"];
const FORMES = ["SARL", "SARL AU", "SA", "SAS", "SNC", "Personne physique", "Coopérative"];
const TONS = ["professionnel et pédagogue", "concis et technique", "chaleureux et accessible"];

function Chips({ options, value, onChange, testid }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => toggle(o)}
          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
            value.includes(o) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"
          }`}
        >
          {value.includes(o) && <Check size={12} className="inline mr-1" />}{o}
        </button>
      ))}
    </div>
  );
}

export default function Onboarding() {
  const france = currentCountry() === "FR";
  const { cabinet, user, refreshCabinet } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    nom: cabinet?.nom || "", ville: cabinet?.ville || "", agrement_oec: cabinet?.agrement_oec || "",
    signature: cabinet?.signature || cabinet?.nom || "",
    raison_sociale: cabinet?.raison_sociale || "", forme_juridique: cabinet?.forme_juridique || "",
    ice: cabinet?.ice || "", identifiant_fiscal: cabinet?.identifiant_fiscal || "",
    rc: cabinet?.rc || "", patente_tp: cabinet?.patente_tp || "", cnss: cabinet?.cnss || "",
    adresse: cabinet?.adresse || "", telephone: cabinet?.telephone || "", representant_legal: cabinet?.representant_legal || "",
    secteurs: [], types_clients: [], regimes_fiscaux: [], formes_juridiques: [],
    ton: TONS[0], notes: "",
  });
  const set = (k, v) => setF({ ...f, [k]: v });

  const finish = async () => {
    if (!f.nom.trim()) return toast.error("Le nom du cabinet est requis");
    setSaving(true);
    try {
      const fields = ["nom", "ville", "agrement_oec", "signature", "telephone", "adresse", "ton", "notes"];
      await api.post("/cabinet/onboarding", france ? Object.fromEntries(fields.map((key) => [key, f[key]])) : f);
      await refreshCabinet();
      toast.success("Votre assistant est configuré pour votre cabinet");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const steps = [
    {
      title: "Bienvenue sur Ninyaa",
      body: (
        <div className="space-y-4" data-testid="ob-welcome">
          <p className="text-sm text-foreground">Ninyaa est le <strong>copilote de votre cabinet</strong> : il automatise les tâches quotidiennes de vos collaborateurs pour qu'ils <strong>valident et contrôlent</strong> au lieu de ressaisir.</p>
          <div className="rounded-lg bg-muted/40 border border-border p-4 space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Votre quotidien, automatisé</div>
            <ul className="text-sm space-y-1.5">
              <li className="flex gap-2"><Check size={16} className="text-primary mt-0.5 shrink-0" /> Importez une facture → l'écriture comptable est proposée, prête à valider.</li>
              <li className="flex gap-2"><Check size={16} className="text-primary mt-0.5 shrink-0" /> Importez un grand livre → révision des comptes, TVA (CA3), lettrage et rapprochement préparés.</li>
              <li className="flex gap-2"><Check size={16} className="text-primary mt-0.5 shrink-0" /> Exportez le journal (Excel / FEC) et le dossier de révision PDF prêt à signer.</li>
              <li className="flex gap-2"><Check size={16} className="text-primary mt-0.5 shrink-0" /> Recevez des rappels d'échéances TVA au bon moment.</li>
            </ul>
          </div>
          <p className="text-xs text-muted-foreground">En 4 étapes rapides, nous personnalisons l'assistant à VOTRE cabinet (identité, clients, méthodologie). C'est parti !</p>
        </div>
      ),
    },
    {
      title: "Identité du cabinet",
      body: (
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Nom du cabinet</Label><Input data-testid="ob-nom" value={f.nom} onChange={(e) => set("nom", e.target.value)} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Ville</Label><Input data-testid="ob-ville" value={f.ville} onChange={(e) => set("ville", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>{france ? "Numéro d’inscription à l’Ordre" : "Agrément OEC"}</Label><Input data-testid="ob-agrement" value={f.agrement_oec} onChange={(e) => set("agrement_oec", e.target.value)} placeholder={france ? "Référence d’inscription du cabinet" : "OEC-2026-…"} /></div>
          </div>
          <div className="space-y-1.5"><Label>Signature (bas des réponses et rapports)</Label><Input data-testid="ob-signature" value={f.signature} onChange={(e) => set("signature", e.target.value)} /></div>
        </div>
      ),
    },
    {
      title: "Conformité & identité légale (KYC)",
      body: (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">Ces informations figurent sur vos dossiers de travail et rapports, et sécurisent la conformité de votre cabinet.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Raison sociale</Label><Input data-testid="kyc-raison-sociale" value={f.raison_sociale} onChange={(e) => set("raison_sociale", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Forme juridique</Label><Input data-testid="kyc-forme-juridique" value={f.forme_juridique} onChange={(e) => set("forme_juridique", e.target.value)} placeholder="SARL, SA, SAS…" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>ICE</Label><Input data-testid="kyc-ice" value={f.ice} onChange={(e) => set("ice", e.target.value)} placeholder="Identifiant Commun de l'Entreprise" /></div>
            <div className="space-y-1.5"><Label>Identifiant Fiscal (IF)</Label><Input data-testid="kyc-if" value={f.identifiant_fiscal} onChange={(e) => set("identifiant_fiscal", e.target.value)} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Registre de Commerce (RC)</Label><Input data-testid="kyc-rc" value={f.rc} onChange={(e) => set("rc", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Patente / Taxe Professionnelle</Label><Input data-testid="kyc-patente" value={f.patente_tp} onChange={(e) => set("patente_tp", e.target.value)} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>N° CNSS</Label><Input data-testid="kyc-cnss" value={f.cnss} onChange={(e) => set("cnss", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Téléphone</Label><Input data-testid="kyc-tel" value={f.telephone} onChange={(e) => set("telephone", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Adresse du siège</Label><Input data-testid="kyc-adresse" value={f.adresse} onChange={(e) => set("adresse", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Représentant légal</Label><Input data-testid="kyc-representant" value={f.representant_legal} onChange={(e) => set("representant_legal", e.target.value)} /></div>
        </div>
      ),
    },
    {
      title: "Vos clients & spécialités",
      body: (
        <div className="space-y-5">
          <div><Label className="mb-2 block">Secteurs de vos clients</Label><Chips options={SECTEURS} value={f.secteurs} onChange={(v) => set("secteurs", v)} testid="ob-secteurs" /></div>
          <div><Label className="mb-2 block">Types de clients</Label><Chips options={CLIENTS} value={f.types_clients} onChange={(v) => set("types_clients", v)} testid="ob-clients" /></div>
          <div><Label className="mb-2 block">Régimes fiscaux traités</Label><Chips options={REGIMES} value={f.regimes_fiscaux} onChange={(v) => set("regimes_fiscaux", v)} testid="ob-regimes" /></div>
          <div><Label className="mb-2 block">Formes juridiques fréquentes</Label><Chips options={FORMES} value={f.formes_juridiques} onChange={(v) => set("formes_juridiques", v)} testid="ob-formes" /></div>
        </div>
      ),
    },
    {
      title: "Personnalité de l'assistant",
      body: (
        <div className="space-y-5">
          <div><Label className="mb-2 block">Ton de l'assistant</Label><Chips options={TONS} value={[f.ton]} onChange={(v) => set("ton", v[v.length - 1] || TONS[0])} testid="ob-ton" /></div>
          <div className="space-y-1.5">
            <Label>Méthodologie & positions propres à votre cabinet</Label>
            <Textarea data-testid="ob-notes" rows={5} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Ex : Nous privilégions l'amortissement linéaire ; clientèle majoritairement soumise à la TVA mensuelle ; vigilance particulière sur les comptes courants d'associés…" />
            <p className="text-xs text-muted-foreground">Ces éléments rendent l'assistant spécifique à VOTRE cabinet et non générique.</p>
          </div>
        </div>
      ),
    },
    {
      title: "Ce que Ninyaa fait pour vous",
      body: (
        <div className="space-y-3" data-testid="ob-features">
          <p className="text-sm text-muted-foreground">Voici les outils que vos collaborateurs utiliseront au quotidien. Chacun se lance en déposant un fichier ou en posant une question.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              [ChatCircleText, "Copilote", "Réponses comptables & juridiques sourcées (CGNC, CGI) en quelques secondes."],
              [MagnifyingGlass, "Révision 360°", "Revue des comptes automatique à partir du grand livre + dossier PDF à signer."],
              [Percent, "TVA — CA3", "Déclaration de TVA calculée automatiquement (à payer / crédit à reporter)."],
              [LinkSimple, "Lettrage", "Factures et règlements des clients/fournisseurs rapprochés automatiquement."],
              [Bank, "Rapprochement bancaire", "Relevé pointé automatiquement, écarts isolés (frais, chèques en attente)."],
              [Receipt, "Saisie OCR de factures", "Facture (PDF/photo) → écriture équilibrée proposée, en série si besoin."],
              [BookOpen, "Journal & exports", "Écritures tracées, n° de pièce, export Excel / FEC (Sage) par période."],
              [BellRinging, "Rappels d'échéances", "Alerte TVA au bon moment, ouvre le journal sur la bonne période en un clic."],
            ].map(([Icon, titre, desc], i) => (
              <div key={i} className="rounded-lg border border-border p-3 flex gap-3">
                <Icon size={20} className="text-primary shrink-0 mt-0.5" weight="fill" />
                <div>
                  <div className="text-sm font-semibold">{titre}</div>
                  <div className="text-xs text-muted-foreground">{desc}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Cliquez sur « Activer mon assistant » pour démarrer. Vous retrouverez tous ces outils via le bouton « + » de la conversation.</p>
        </div>
      ),
    },
  ];

  if (user?.role !== "partner") return null;
  const cur = steps[step];

  const visibleSteps = france ? [
    { title: "Bienvenue dans votre espace", body: <div className="space-y-3 text-sm" data-testid="ob-welcome"><p>Retrouvez vos dossiers, échangez vos pièces et suivez les travaux de votre équipe.</p><p>Commencez par créer un dossier. Vous pourrez ensuite joindre ses documents et préparer vos premiers travaux depuis la conversation.</p></div> },
    steps[1], steps[4],
  ] : steps;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-8 bg-muted/30">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <Logo className="h-11 w-11" rounded="rounded-xl" />
          <div>
            <div className="font-heading font-extrabold text-xl tracking-tight">Prise en main de Ninyaa</div>
            <div className="text-sm text-muted-foreground">Bienvenue, personnalisation de votre assistant et découverte des outils — étape {step + 1}/{visibleSteps.length}</div>
          </div>
        </div>

        <div className="flex gap-1.5 mb-6">
          {visibleSteps.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-border"}`} />
          ))}
        </div>

        <div className="border border-border rounded-lg bg-background p-6">
          <h2 className="font-heading font-bold text-lg mb-5 flex items-center gap-2">
            <ShieldCheck size={18} className="text-primary" /> {cur.title}
          </h2>
          {cur.body}

          <div className="flex items-center justify-between mt-8 pt-5 border-t border-border">
            <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="ob-back">
              <ArrowLeft size={16} className="mr-1.5" /> Retour
            </Button>
            {step < visibleSteps.length - 1 ? (
              <Button onClick={() => setStep(step + 1)} data-testid="ob-next">Continuer <ArrowRight size={16} className="ml-1.5" /></Button>
            ) : (
              <Button onClick={finish} disabled={saving} data-testid="ob-finish">
                {saving ? "Configuration…" : "Activer mon assistant"} <Check size={16} className="ml-1.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
